import { Ability } from '@casl/ability';
import { PaginatedQueryResult, useTableState } from '@dispatch/shared/hooks';
import {
  Actions,
  ResultStatus,
  Role,
  RolesListFilter,
  Subjects,
} from '@dispatch/shared/types';
import {
  boldLinkColumn,
  CreateNewEntityButton,
  dateColumn,
  PageComponent,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface RolesProps {
  rolesData: PaginatedQueryResult<Role[], unknown>;
  onSearch: (value: string) => void;
  filters: RolesListFilter;
  onFilterChange: (filters: RolesListFilter) => void;
  status: ResultStatus;
  permissions: Ability<[Actions, Subjects]> | undefined;
}

export function Roles({
  rolesData,
  onSearch,
  onFilterChange,
  filters,
  status,
  permissions,
}: RolesProps) {
  const { tableState, setTableState } = useTableState();
  const [data, setData] = useState<Role[]>([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (rolesData && rolesData?.data) {
      setData([...rolesData.data]);
    }
  }, [rolesData]);

  const columns: GridColDef[] = [
    boldLinkColumn<Role, string>(
      {
        field: 'name',
        headerName: 'Name',
        sortable: false,
      },
      (role, _rowValue) => `/admin/roles/${role?.id}`
    ),
    textColumn({
      field: 'activeUsers',
      headerName: 'Active Users with Role',
      valueGetter: (params) => {
        return `${params?.row?.userCount || '0'}`;
      },
      sortable: false,
    }),
    textColumn({
      field: 'rightsCount',
      headerName: 'Amount of Rights',
      valueGetter: (params) => {
        const size = params?.row?.permissions?.length;
        return `${size}`;
      },
      sortable: false,
    }),
    dateColumn({
      field: 'createdAt',
      headerName: 'Created',
      maxWidth: 170,
      sortable: false,
      valueGetter: (params) => new Date(params.row.createdAt),
    }),
  ];

  const tableArgs: TableProps<Role> = {
    tableState: {
      columnModel: tableState['roles'].columnModel,
      pinnedColumns: {
        ...tableState['roles'].pinnedColumns,
      },
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        roles: { ...tableState['roles'], columnModel: newModel },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        roles: { ...tableState['roles'], pinnedColumns: newPinned },
      });
    },
    rows: data,
    columns,
    status: status,
    emptyEntityName: 'Role',
    serverPaging: {
      ...rolesData,
      onPageSizeChange: (newPageSize) => {
        onFilterChange({ ...filters, pageSize: newPageSize });
      },
    },
  };

  return (
    <PageComponent
      title="Roles"
      onSearch={onSearch}
      searchPlaceholder="Name"
      actions={
        permissions?.can('put', 'roles') && (
          <CreateNewEntityButton
            onClick={() => navigate('/admin/roles/add')}
            entityName="Role"
          />
        )
      }
    >
      <Box width="100%">
        <TableComponent {...tableArgs} />
      </Box>
    </PageComponent>
  );
}

export default Roles;
